// It abstracts away the complexity of the configuration setting via exports
// Each parameter is set separately, so here we try to provide batching for
// changes and possibility to to get parameters when they are set

import { Result } from '~/ts-utils';
import { initDebouncedPubSub } from '~commons/debounced-pub-sub';

type Resource = {
  id: string;
  link?: string;
};

type ConfigMultiResource = {
  namespace: string;
  resourceList: Resource[];
};

const sanitizeResourceList = (list: Resource[]) => list.filter((r) => !!r.id);

export const createConfigurationStoreMulti = ({
  defaultNamespace,
  defaultResourceList = [],
  defaultWaitForResourceId,
}: {
  defaultNamespace: string;
  defaultResourceList?: Resource[];
  defaultWaitForResourceId: boolean;
}) => {
  let configState:
    | { type: 'initialized'; value: ConfigMultiResource }
    | {
        type: 'not_initialized';
        value: ConfigMultiResource;
      } = {
    type: 'not_initialized',
    value: {
      namespace: defaultNamespace,
      resourceList: defaultResourceList,
    },
  };
  const pubSub = initDebouncedPubSub<ConfigMultiResource>();

  return {
    setNamespace: (namespace: string) => {
      if (!namespace) {
        throw new Error('Namespace cannot be empty');
      }
      configState.value.namespace = namespace;
      if (configState.type === 'initialized') {
        pubSub.publish(configState.value);
      }
    },
    setResourceList: (resourceList?: Resource[]) => {
      configState = {
        type: 'initialized',
        value: {
          ...configState.value,
          resourceList: sanitizeResourceList(resourceList ?? []),
        },
      };
      pubSub.publish(configState.value);
    },
    getConfiguration: async (): Promise<Result<ConfigMultiResource>> => {
      if (configState.type === 'initialized') {
        return { type: 'ok', value: configState.value };
      }
      return defaultWaitForResourceId
        ? pubSub.waitForFirstPub()
        : { type: 'ok', value: configState.value };
    },
    getConfigState: () => ({ ...configState }),
    subscribe: pubSub.subscribe,
  };
};
